"use client";

import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartCard } from "./chart-card";
import type { TrendPoint } from "@/types/analytics";
import type { TimeBucketGranularity } from "@/types/filters";

interface RequestTrendChartProps {
  points: TrendPoint[];
  granularity: TimeBucketGranularity;
  isLoading: boolean;
  onSelectBucket: (bucketStart: string, granularity: TimeBucketGranularity) => void;
}

export function RequestTrendChart({ points, granularity, isLoading, onSelectBucket }: RequestTrendChartProps) {
  return (
    <ChartCard
      title="Requests over time"
      description="All requests matching the current filters, bucketed by time."
      isLoading={isLoading}
      isEmpty={!isLoading && points.length === 0}
      emptyMessage="No requests were found for this window."
    >
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={points}
          onClick={(state) => {
            const label = state?.activeLabel;
            if (typeof label === "string") onSelectBucket(label, granularity);
          }}
        >
          <defs>
            <linearGradient id="requestsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--chart-2)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--chart-2)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="bucketLabel"
            tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
            axisLine={{ stroke: "var(--border)" }}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
            axisLine={false}
            tickLine={false}
            width={36}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ stroke: "var(--chart-2)", strokeWidth: 1 }}
            contentStyle={{
              background: "var(--popover)",
              color: "var(--popover-foreground)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Area
            type="monotone"
            dataKey="totalRequests"
            name="Requests"
            stroke="var(--chart-2)"
            strokeWidth={2}
            fill="url(#requestsFill)"
            activeDot={{ r: 4, cursor: "pointer" }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
